import type { Card, ProductType } from "@/context/AppContext";
import {
  getFamiliesFromInventory,
  normalizeName,
} from "@/features/pokemonFamilyMatcher";
import {
  buildWeightedProfile,
  pickWeightedBundle,
  summarizeBundle,
  type BundleSummary,
  type MixOverride,
  type PickReason,
  type WeightProfileName,
} from "@/features/bundleWeightedPicker";

export type { MixOverride, WeightProfileName };

// ─── Types ────────────────────────────────────────────────────────────────────

export type BundleStrategyId =
  | "same-set"
  | "rarity-tier"
  | "pokemon-type"
  | "pokemon-trainer"
  | "pokemon-energy"
  | "pokemon-series"
  | "pokemon-family"
  // ── Studio strategies (generators live in productOpportunityEngine.ts) ──
  | "random-value-lot"
  | "set-builder-lot"
  | "character-theme"
  | "starter-kid-pack"
  | "casual-prebuilt-deck"
  | "deck-core"
  | "deck-upgrade-kit"
  | "trainer-staples-kit"
  | "energy-kit"
  // ── User-defined bundle templates (generator lives in bundleTemplateEngine.ts) ──
  | "user-template";

/**
 * Lower bound for "available" bundles, expressed as a fraction of the
 * strategy's targetCount. A bundle whose card-count is at or above this
 * fraction of its target qualifies as a partial build (≥80% available);
 * at or above 100% it is fully available.
 */
export const PARTIAL_BUILD_THRESHOLD = 0.8;

/**
 * Returns the floor card-count for partial bundles given a target.
 * `qty < partialFloor(target)` should be skipped by every generator.
 */
export function partialFloor(target: number): number {
  return Math.max(1, Math.floor(target * PARTIAL_BUILD_THRESHOLD));
}

/** Availability % capped at 100, rounded to nearest integer. */
export function availabilityPctOf(qty: number, target: number): number {
  if (target <= 0) return 0;
  return Math.min(100, Math.round((qty / target) * 100));
}

export interface BundleStrategySetting {
  id: BundleStrategyId;
  enabled: boolean;
  priority: number;
}

export type BundleSortMode = "profit" | "speed" | "balanced";

export type BundleSellSpeed = "Fast" | "Medium" | "Slow" | "Unknown";
export type BundleConfidence = "High" | "Medium" | "Low" | "Unknown";

export interface BundleSettings {
  globalEnabled: boolean;
  strategies: BundleStrategySetting[];
  sameSetMinCards: number;
  sortMode: BundleSortMode;
  /**
   * Per-profile tweaks layered on top of the hardcoded `BASE_PROFILES` in
   * bundleWeightedPicker. Used by user-template selection profiles (bulk /
   * standard / premium) so power users can shift the default rarity mix
   * without editing code. Absent = use base profile weights as-is.
   */
  mixOverrides?: Partial<Record<WeightProfileName, MixOverride>>;
}

export interface BundleSuggestion {
  id: string;
  strategyId: BundleStrategyId;
  game: string;
  bundleType: string;
  label: string;
  cards: Card[];
  cardCount: number;
  estimatedMarketValue: number;
  suggestedProductType: ProductType;
  suggestedTitle: string;
  confidenceScore: number;
  /** Expected sale price = estimatedMarketValue * MARKET_TARGET_RATIO. */
  expectedSalePrice: number;
  /** Sum of card.costBasis * card.quantity. */
  estimatedCostBasis: number;
  /** expectedSalePrice * (1 - feeRate) - estimatedCostBasis - shippingReserve. */
  expectedProfit: number;
  /** Populated by enrichBundleSuggestions; defaults to "Unknown". */
  sellSpeed: BundleSellSpeed;
  /** Populated by enrichBundleSuggestions; defaults to "Unknown". */
  confidence: BundleConfidence;
  /** Number of historical sold products in this (strategy, productType). 0 = no history. */
  historicalSampleSize: number;
  /** Average days-to-sell for matching historical sales (null if no history). */
  historicalAvgDaysToSell: number | null;
  /** Strategy's "100% built" goal — the cardCount at which this bundle is fully available. */
  targetCount: number;
  /** 0..100, capped at 100. cardCount/targetCount * 100. */
  availabilityPct: number;
}

// Pricing assumptions for profit estimate. Aligned with create.tsx MARKET_TARGET_RATIO.
const MARKET_TARGET_RATIO = 0.8;
const FEE_RATE = 0.13; // ~eBay 12.9% final value fee
const SHIPPING_RESERVE = 1.0;

function buildEconomics(cards: Card[], market: number) {
  const expectedSalePrice = market * MARKET_TARGET_RATIO;
  const estimatedCostBasis = cards.reduce(
    (s, c) => s + (c.costBasis || 0) * c.quantity,
    0,
  );
  const expectedProfit =
    expectedSalePrice * (1 - FEE_RATE) - estimatedCostBasis - SHIPPING_RESERVE;
  return { expectedSalePrice, estimatedCostBasis, expectedProfit };
}

// ─── Strategy metadata ────────────────────────────────────────────────────────

export interface StrategyMeta {
  id: BundleStrategyId;
  name: string;
  description: string;
  requiresPokemonApiData: boolean;
  defaultMinCards: number;
}

export const STRATEGY_META: StrategyMeta[] = [
  {
    id: "same-set",
    name: "Same Set",
    description:
      "Groups unlisted cards from the same set into a set lot. Works for all games.",
    requiresPokemonApiData: false,
    defaultMinCards: 10,
  },
  {
    id: "rarity-tier",
    name: "Rarity Tier",
    description:
      "Groups cards by rarity bucket (Holo/Reverse, Rare, Common/Uncommon, Hit). Works for all games.",
    requiresPokemonApiData: false,
    defaultMinCards: 8,
  },
  {
    id: "pokemon-type",
    name: "Pokémon: By Type",
    description:
      "Groups Pokémon cards by energy type (Fire, Water, Grass…). Requires API lookup data.",
    requiresPokemonApiData: true,
    defaultMinCards: 6,
  },
  {
    id: "pokemon-trainer",
    name: "Pokémon: Trainer Lot",
    description:
      "Bundles all Trainer, Item, and Supporter cards together. Requires API lookup data.",
    requiresPokemonApiData: true,
    defaultMinCards: 10,
  },
  {
    id: "pokemon-energy",
    name: "Pokémon: Energy Lot",
    description:
      "Bundles all Energy cards into a single lot. Requires API lookup data.",
    requiresPokemonApiData: true,
    defaultMinCards: 10,
  },
  {
    id: "pokemon-series",
    name: "Pokémon: By Series",
    description:
      "Groups cards spanning the same era (e.g., Scarlet & Violet Series). Requires API lookup data.",
    requiresPokemonApiData: true,
    defaultMinCards: 15,
  },
  {
    id: "pokemon-family",
    name: "Pokémon: Evolution & Theme Family",
    description:
      "Groups Pokémon cards into evolution lines and themed families (Charizard line, Eeveelutions, Legendary Birds…). Surfaces complete lines automatically.",
    requiresPokemonApiData: true,
    defaultMinCards: 6,
  },
];

// ─── Default settings ─────────────────────────────────────────────────────────

export const DEFAULT_BUNDLE_SETTINGS: BundleSettings = {
  globalEnabled: true,
  sameSetMinCards: 10,
  sortMode: "balanced",
  strategies: STRATEGY_META.map((m, i) => ({
    id: m.id,
    enabled: true,
    priority: i,
  })),
  mixOverrides: {},
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function totalMarket(cards: Card[]): number {
  return cards.reduce((s, c) => s + (c.marketPrice || 0) * c.quantity, 0);
}

function totalQty(cards: Card[]): number {
  return cards.reduce((s, c) => s + c.quantity, 0);
}

function confidenceScore(cards: Card[], minCards: number): number {
  const qty = totalQty(cards);
  const countScore = Math.min(1, qty / (minCards * 2));
  const market = totalMarket(cards);
  const valueScore = market > 0 ? Math.min(1, market / 20) : 0;
  return Math.round((countScore * 0.6 + valueScore * 0.4) * 100);
}

function dominantCondition(cards: Card[]): string {
  const counts: Record<string, number> = {};
  for (const c of cards) {
    counts[c.condition] = (counts[c.condition] ?? 0) + c.quantity;
  }
  return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? "NM";
}

function gameLabel(cards: Card[]): string {
  const g = cards[0]?.game ?? "TCG";
  if (g === "Pokemon") return "Pokémon";
  if (g === "Magic") return "Magic: The Gathering";
  if (g === "Yu-Gi-Oh") return "Yu-Gi-Oh!";
  return g;
}

/**
 * Maps each standard product type to its canonical lot-size target.
 * "Custom" is intentionally absent — those bundles use the actual card qty.
 */
const PRODUCT_TYPE_LOT_TARGET: Partial<Record<ProductType, number>> = {
  "100-card Random Lot": 100,
  "50-card Themed Lot": 50,
  "25-card Trainer Lot": 25,
  "Holo/Reverse Bundle": 10,
  "Mystery Pack": 20,
  "Starter/Kid Pack": 50,
};

/**
 * Cap a card pool to `target` total quantity (sum of card.quantity).
 * Sorts by descending market price so the highest-value cards are picked first.
 * If strict qty-fitting produces nothing (all cards have qty > remaining space),
 * falls back to the sorted list verbatim so the caller always gets something.
 */
function capToTarget(pool: Card[], target: number): Card[] {
  if (totalQty(pool) <= target) return pool;
  const sorted = [...pool].sort(
    (a, b) => (b.marketPrice ?? 0) - (a.marketPrice ?? 0),
  );
  const chosen: Card[] = [];
  let qty = 0;
  for (const c of sorted) {
    if (qty >= target) break;
    if (qty + c.quantity > target) continue;
    chosen.push(c);
    qty += c.quantity;
  }
  return chosen.length > 0 ? chosen : sorted;
}

// ─── Strategy: Same Set ───────────────────────────────────────────────────────

export function generateSetBundles(
  cards: Card[],
  usedIds: Set<string>,
  lowerBound: number,
  _targetCount: number = lowerBound,
): BundleSuggestion[] {
  const available = cards.filter((c) => !usedIds.has(c.id));
  const bySet = new Map<string, Card[]>();
  for (const c of available) {
    const key = c.setName || "Unknown Set";
    const arr = bySet.get(key) ?? [];
    arr.push(c);
    bySet.set(key, arr);
  }

  const results: BundleSuggestion[] = [];
  for (const [setName, setCards] of bySet.entries()) {
    const qty = totalQty(setCards);
    if (qty < lowerBound) continue;

    const game = setCards[0]?.game ?? "Pokemon";
    const gameStr = gameLabel(setCards);

    const productType: ProductType =
      qty >= 80
        ? "100-card Random Lot"
        : qty >= 40
          ? "50-card Themed Lot"
          : "Custom";

    const lotTarget = PRODUCT_TYPE_LOT_TARGET[productType] ?? qty;
    const cappedCards = capToTarget(setCards, lotTarget);
    const cappedQty = totalQty(cappedCards);
    const cond = dominantCondition(cappedCards);
    const market = totalMarket(cappedCards);

    const title = `${cappedQty}-Card ${setName} Lot – ${cond}/Mixed – ${gameStr} TCG`;

    results.push({
      id: `same-set-${setName}`,
      strategyId: "same-set",
      game,
      bundleType: "Set Lot",
      label: setName,
      cards: cappedCards,
      cardCount: cappedQty,
      estimatedMarketValue: market,
      suggestedProductType: productType,
      suggestedTitle: title,
      confidenceScore: confidenceScore(cappedCards, lotTarget),
      ...buildEconomics(cappedCards, market),
      sellSpeed: "Unknown",
      confidence: "Unknown",
      historicalSampleSize: 0,
      historicalAvgDaysToSell: null,
      targetCount: lotTarget,
      availabilityPct: availabilityPctOf(cappedQty, lotTarget),
    });
  }

  return results.sort((a, b) => b.confidenceScore - a.confidenceScore);
}

// ─── Strategy: Rarity Tier ────────────────────────────────────────────────────

type RarityBucket =
  | "Hit / Premium"
  | "Holo & Reverse"
  | "Rares"
  | "Common / Uncommon";

function rarityBucket(c: Card): RarityBucket {
  // After the two-axis refactor, premium / holo / reverse-holo live on
  // `finish` rather than as rarity values. Check finish first so we still
  // get the same bucketing for what used to be `rarity: "Hit"` etc.
  if (c.finish === "Premium") return "Hit / Premium";
  if (c.finish === "Holo" || c.finish === "Reverse Holo")
    return "Holo & Reverse";
  const rarity = c.rarity;
  if (
    rarity === "Common" ||
    rarity === "Uncommon" ||
    rarity === "Energy" ||
    rarity === "Trainer"
  )
    return "Common / Uncommon";
  return "Rares";
}

const RARITY_PRODUCT_TYPE: Record<RarityBucket, ProductType> = {
  "Hit / Premium": "Premium Single",
  "Holo & Reverse": "Holo/Reverse Bundle",
  Rares: "50-card Themed Lot",
  "Common / Uncommon": "100-card Random Lot",
};

export function generateRarityBundles(
  cards: Card[],
  usedIds: Set<string>,
  lowerBound: number,
  _targetCount: number = lowerBound,
): BundleSuggestion[] {
  const available = cards.filter((c) => !usedIds.has(c.id));
  const byBucket = new Map<RarityBucket, Card[]>();
  for (const c of available) {
    const bucket = rarityBucket(c);
    const arr = byBucket.get(bucket) ?? [];
    arr.push(c);
    byBucket.set(bucket, arr);
  }

  const results: BundleSuggestion[] = [];
  for (const [bucket, bucketCards] of byBucket.entries()) {
    const qty = totalQty(bucketCards);
    if (qty < lowerBound) continue;

    const game = bucketCards[0]?.game ?? "Pokemon";
    const productType = RARITY_PRODUCT_TYPE[bucket];
    const lotTarget = PRODUCT_TYPE_LOT_TARGET[productType] ?? qty;
    const cappedCards = capToTarget(bucketCards, lotTarget);
    const cappedQty = totalQty(cappedCards);
    const cond = dominantCondition(cappedCards);
    const market = totalMarket(cappedCards);
    const gameStr = gameLabel(cappedCards);

    const title = `${cappedQty}-Card ${bucket} Lot – ${cond}/Mixed – ${gameStr} TCG`;

    results.push({
      id: `rarity-${bucket}`,
      strategyId: "rarity-tier",
      game,
      bundleType: "Rarity Lot",
      label: bucket,
      cards: cappedCards,
      cardCount: cappedQty,
      estimatedMarketValue: market,
      suggestedProductType: productType,
      suggestedTitle: title,
      confidenceScore: confidenceScore(cappedCards, lotTarget),
      ...buildEconomics(cappedCards, market),
      sellSpeed: "Unknown",
      confidence: "Unknown",
      historicalSampleSize: 0,
      historicalAvgDaysToSell: null,
      targetCount: lotTarget,
      availabilityPct: availabilityPctOf(cappedQty, lotTarget),
    });
  }

  return results.sort((a, b) => b.confidenceScore - a.confidenceScore);
}

// ─── Strategy: Pokémon by Type ────────────────────────────────────────────────

export function generatePokemonTypeBundles(
  cards: Card[],
  usedIds: Set<string>,
  lowerBound: number,
  targetCount: number = lowerBound,
): BundleSuggestion[] {
  const available = cards.filter(
    (c) =>
      !usedIds.has(c.id) &&
      c.game === "Pokemon" &&
      c.tcgTypes &&
      c.tcgTypes.length > 0,
  );
  const byType = new Map<string, Card[]>();
  for (const c of available) {
    const type = c.tcgTypes![0];
    const arr = byType.get(type) ?? [];
    arr.push(c);
    byType.set(type, arr);
  }

  const results: BundleSuggestion[] = [];
  for (const [type, typeCards] of byType.entries()) {
    const qty = totalQty(typeCards);
    if (qty < lowerBound) continue;

    const lotTarget = PRODUCT_TYPE_LOT_TARGET["50-card Themed Lot"]!;
    const cappedCards = capToTarget(typeCards, lotTarget);
    const cappedQty = totalQty(cappedCards);
    const market = totalMarket(cappedCards);
    const cond = dominantCondition(cappedCards);
    const title = `${cappedQty}-Card ${type} Type Pokémon Lot – ${cond}/Mixed – Great for Deck Building`;

    results.push({
      id: `pokemon-type-${type}`,
      strategyId: "pokemon-type",
      game: "Pokemon",
      bundleType: "Type Bundle",
      label: `${type} Type`,
      cards: cappedCards,
      cardCount: cappedQty,
      estimatedMarketValue: market,
      suggestedProductType: "50-card Themed Lot",
      suggestedTitle: title,
      confidenceScore: confidenceScore(cappedCards, lotTarget),
      ...buildEconomics(cappedCards, market),
      sellSpeed: "Unknown",
      confidence: "Unknown",
      historicalSampleSize: 0,
      historicalAvgDaysToSell: null,
      targetCount: lotTarget,
      availabilityPct: availabilityPctOf(cappedQty, lotTarget),
    });
  }

  return results.sort((a, b) => b.confidenceScore - a.confidenceScore);
}

// ─── Strategy: Pokémon Trainer Lot ───────────────────────────────────────────

export function generatePokemonTrainerBundles(
  cards: Card[],
  usedIds: Set<string>,
  lowerBound: number,
  targetCount: number = lowerBound,
): BundleSuggestion[] {
  const available = cards.filter(
    (c) =>
      !usedIds.has(c.id) &&
      c.game === "Pokemon" &&
      c.tcgSupertype?.toLowerCase() === "trainer",
  );
  const qty = totalQty(available);
  if (qty < lowerBound || available.length === 0) return [];

  const lotTarget = PRODUCT_TYPE_LOT_TARGET["25-card Trainer Lot"]!;
  const cappedCards = capToTarget(available, lotTarget);
  const cappedQty = totalQty(cappedCards);
  const market = totalMarket(cappedCards);
  const cond = dominantCondition(cappedCards);
  const title = `${cappedQty}-Card Trainer/Supporter Pokémon Lot – ${cond} – Ready to Play`;

  return [
    {
      id: "pokemon-trainer",
      strategyId: "pokemon-trainer",
      game: "Pokemon",
      bundleType: "Trainer Lot",
      label: "Trainer & Supporter Cards",
      cards: cappedCards,
      cardCount: cappedQty,
      estimatedMarketValue: market,
      suggestedProductType: "25-card Trainer Lot",
      suggestedTitle: title,
      confidenceScore: confidenceScore(cappedCards, lotTarget),
      ...buildEconomics(cappedCards, market),
      sellSpeed: "Unknown",
      confidence: "Unknown",
      historicalSampleSize: 0,
      historicalAvgDaysToSell: null,
      targetCount: lotTarget,
      availabilityPct: availabilityPctOf(cappedQty, lotTarget),
    },
  ];
}

// ─── Strategy: Pokémon Energy Lot ────────────────────────────────────────────

export function generatePokemonEnergyBundles(
  cards: Card[],
  usedIds: Set<string>,
  lowerBound: number,
  targetCount: number = lowerBound,
): BundleSuggestion[] {
  const available = cards.filter(
    (c) =>
      !usedIds.has(c.id) &&
      c.game === "Pokemon" &&
      c.tcgSupertype?.toLowerCase() === "energy",
  );
  const qty = totalQty(available);
  if (qty < lowerBound || available.length === 0) return [];

  const market = totalMarket(available);
  const cond = dominantCondition(available);
  const title = `${qty}-Card Energy Lot – ${cond} – Pokémon TCG Mixed Energy Cards`;

  return [
    {
      id: "pokemon-energy",
      strategyId: "pokemon-energy",
      game: "Pokemon",
      bundleType: "Energy Lot",
      label: "Energy Cards",
      cards: available,
      cardCount: qty,
      estimatedMarketValue: market,
      suggestedProductType: "Custom",
      suggestedTitle: title,
      confidenceScore: confidenceScore(available, qty),
      ...buildEconomics(available, market),
      sellSpeed: "Unknown",
      confidence: "Unknown",
      historicalSampleSize: 0,
      historicalAvgDaysToSell: null,
      targetCount: qty,
      availabilityPct: 100,
    },
  ];
}

// ─── Strategy: Pokémon by Series ─────────────────────────────────────────────

export function generatePokemonSeriesBundles(
  cards: Card[],
  usedIds: Set<string>,
  lowerBound: number,
  targetCount: number = lowerBound,
): BundleSuggestion[] {
  const available = cards.filter(
    (c) => !usedIds.has(c.id) && c.game === "Pokemon" && c.tcgSeries,
  );
  const bySeries = new Map<string, Card[]>();
  for (const c of available) {
    const series = c.tcgSeries!;
    const arr = bySeries.get(series) ?? [];
    arr.push(c);
    bySeries.set(series, arr);
  }

  const results: BundleSuggestion[] = [];
  for (const [series, seriesCards] of bySeries.entries()) {
    const qty = totalQty(seriesCards);
    if (qty < lowerBound) continue;

    const productType: ProductType =
      qty >= 80 ? "100-card Random Lot" : "50-card Themed Lot";
    const lotTarget = PRODUCT_TYPE_LOT_TARGET[productType]!;
    const cappedCards = capToTarget(seriesCards, lotTarget);
    const cappedQty = totalQty(cappedCards);
    const market = totalMarket(cappedCards);
    const cond = dominantCondition(cappedCards);
    const seriesShort = series.replace(" Series", "").trim();
    const title = `${cappedQty}-Card ${seriesShort} Series Lot – ${cond}/Mixed – Pokémon TCG`;

    results.push({
      id: `pokemon-series-${series}`,
      strategyId: "pokemon-series",
      game: "Pokemon",
      bundleType: "Series Bundle",
      label: series,
      cards: cappedCards,
      cardCount: cappedQty,
      estimatedMarketValue: market,
      suggestedProductType: productType,
      suggestedTitle: title,
      confidenceScore: confidenceScore(cappedCards, lotTarget),
      ...buildEconomics(cappedCards, market),
      sellSpeed: "Unknown",
      confidence: "Unknown",
      historicalSampleSize: 0,
      historicalAvgDaysToSell: null,
      targetCount: lotTarget,
      availabilityPct: availabilityPctOf(cappedQty, lotTarget),
    });
  }

  return results.sort((a, b) => b.confidenceScore - a.confidenceScore);
}

// ─── Strategy: Pokémon Evolution / Theme Family ──────────────────────────────

export function generatePokemonFamilyBundles(
  cards: Card[],
  usedIds: Set<string>,
  lowerBound: number,
  targetCount: number = lowerBound,
): BundleSuggestion[] {
  const available = cards.filter(
    (c) => !usedIds.has(c.id) && c.game === "Pokemon",
  );
  if (available.length === 0) return [];

  const matches = getFamiliesFromInventory(available);
  const results: BundleSuggestion[] = [];

  for (const match of matches) {
    const qty = totalQty(match.matchedCards);
    if (qty < lowerBound && !match.hasFullLine) continue;
    if (match.matchedCards.length === 0) continue;

    const productType: ProductType =
      qty >= 50 ? "50-card Themed Lot" : "Custom";
    const lotTarget = PRODUCT_TYPE_LOT_TARGET[productType] ?? qty;
    const cappedCards = capToTarget(match.matchedCards, lotTarget);
    const cappedQty = totalQty(cappedCards);
    const market = totalMarket(cappedCards);
    const cond = dominantCondition(cappedCards);
    const completeTag = match.hasFullLine ? " — Complete Bundle" : " Bundle";
    const title = `${match.family.label}${completeTag} (${cappedQty} cards) – ${cond}/Mixed – Pokémon TCG`;

    results.push({
      id: `pokemon-family-${match.family.familyId}`,
      strategyId: "pokemon-family",
      game: "Pokemon",
      bundleType: match.hasFullLine ? "Complete Family Lot" : "Family Lot",
      label: match.family.label,
      cards: cappedCards,
      cardCount: cappedQty,
      estimatedMarketValue: market,
      suggestedProductType: productType,
      suggestedTitle: title,
      confidenceScore: confidenceScore(cappedCards, lotTarget),
      ...buildEconomics(cappedCards, market),
      sellSpeed: "Unknown",
      confidence: "Unknown",
      historicalSampleSize: 0,
      historicalAvgDaysToSell: null,
      targetCount: lotTarget,
      availabilityPct: match.hasFullLine
        ? 100
        : availabilityPctOf(cappedQty, lotTarget),
    });
  }

  return results.sort((a, b) => b.confidenceScore - a.confidenceScore);
}

// ─── Default-listing-type classification ─────────────────────────────────────
//
// Pure helpers used by `pickDefaultListingType` to choose between Auction and
// Fixed Price when the Go Live form opens fresh on a product (no relist /
// sell-similar carry-over).

/**
 * Curated list of Pokémon names that consistently command auction interest.
 * Kept intentionally small and conservative so false positives are rare.
 */
export const POPULAR_POKEMON: readonly string[] = [
  "Charizard",
  "Pikachu",
  "Mewtwo",
  "Mew",
  "Lugia",
  "Ho-Oh",
  "Rayquaza",
  "Umbreon",
  "Eevee",
  "Sylveon",
  "Espeon",
  "Gengar",
  "Dragonite",
  "Greninja",
  "Lucario",
  "Garchomp",
  "Gyarados",
  "Snorlax",
  "Blastoise",
  "Venusaur",
  "Tyranitar",
  "Giratina",
  "Dialga",
  "Palkia",
];

/** Coefficient-of-variation threshold used by the variance fallback. */
export const VALUE_VARIANCE_AUCTION_THRESHOLD = 0.6;

/** Card-count threshold for "bulk" classification. */
export const BULK_CARD_THRESHOLD = 50;

const POPULAR_POKEMON_KEYS = new Set(
  POPULAR_POKEMON.map((n) => normalizeName(n)),
);

/** Returns the first matched popular Pokémon name found in the cards (display form), or null. */
export function findPopularPokemon(cards: Card[]): string | null {
  for (const c of cards) {
    if (c.game !== "Pokemon") continue;
    const tokens = normalizeName(c.name).split(/\s+/).filter(Boolean);
    for (const t of tokens) {
      if (POPULAR_POKEMON_KEYS.has(t)) {
        const match = POPULAR_POKEMON.find((p) => normalizeName(p) === t);
        if (match) return match;
      }
    }
  }
  return null;
}

export function containsPopularPokemon(cards: Card[]): boolean {
  return findPopularPokemon(cards) !== null;
}

/** True when the bundle contains both Holo and Reverse Holo cards. */
export function containsHoloReverseMix(cards: Card[]): boolean {
  let hasHolo = false;
  let hasReverse = false;
  for (const c of cards) {
    if (c.finish === "Holo") hasHolo = true;
    else if (c.finish === "Reverse Holo") hasReverse = true;
    if (hasHolo && hasReverse) return true;
  }
  return false;
}

/**
 * Returns a short label describing an evolution line present in the bundle,
 * or null when none is detected.
 *
 * Detection is layered, most-specific first, and stays conservative so we
 * don't misclassify random Pokémon collections as evolution lines:
 *
 *  1. **Curated families** (`getFamiliesFromInventory`) — recognises named
 *     evolution families (Charmander → Charizard, Bulbasaur → Venusaur, etc.)
 *     defined in `pokemonThemeMaps.ts`. Wins when a full coreLine is present.
 *
 *  2. **Name-stem fallback** — for Pokémon outside the curated map, two
 *     or more *different* Pokémon names whose first words share a ≥5
 *     character case-insensitive prefix (e.g., "Magnemite"/"Magneton"
 *     share "Magne", "Charmander"/"Charmeleon" share "Charm") count as
 *     an evolution line. The prefix must also cover at least half of
 *     the shorter name to keep accidental short-prefix collisions like
 *     "Pidove"/"Pidgey" from triggering. We deliberately require shared
 *     name DNA — not just mixed evolution-stage subtypes — so an
 *     arbitrary mix of unrelated Basic + Stage 1 cards in a bulk lot
 *     does *not* trip the heuristic.
 */
export function findEvolutionLine(cards: Card[]): string | null {
  const pokemonCards = cards.filter((c) => c.game === "Pokemon");
  if (pokemonCards.length === 0) return null;

  // 1. Curated families — re-tag listingStatus so packaged ("In Package")
  // cards still flow through `getFamiliesFromInventory`'s unlisted filter.
  const eligible = pokemonCards.map((c) => ({
    ...c,
    listingStatus: "Unlisted" as const,
  }));
  const matches = getFamiliesFromInventory(eligible);
  for (const m of matches) {
    if (m.family.themeType === "evolution_family" && m.hasFullLine) {
      return m.family.label;
    }
  }

  // 2. Name-stem fallback. Filter to actual Pokémon (drop Trainer/Energy)
  // when supertype is known, then look for two distinct first words that
  // share a sufficiently long prefix.
  const realPokemon = pokemonCards.filter(
    (c) =>
      !c.tcgSupertype ||
      c.tcgSupertype.toLowerCase() === "pokémon" ||
      c.tcgSupertype.toLowerCase() === "pokemon",
  );
  const distinctFirstWords: { display: string; word: string }[] = [];
  const seen = new Set<string>();
  for (const c of realPokemon) {
    const norm = normalizeName(c.name);
    const firstWord = norm.split(/\s+/)[0];
    if (!firstWord || firstWord.length < 6) continue;
    if (seen.has(firstWord)) continue;
    seen.add(firstWord);
    distinctFirstWords.push({ display: c.name, word: firstWord });
  }

  const MIN_PREFIX = 5;
  for (let i = 0; i < distinctFirstWords.length; i++) {
    for (let j = i + 1; j < distinctFirstWords.length; j++) {
      const a = distinctFirstWords[i];
      const b = distinctFirstWords[j];
      let common = 0;
      const max = Math.min(a.word.length, b.word.length);
      while (common < max && a.word[common] === b.word[common]) common++;
      if (common < MIN_PREFIX) continue;
      // Require the prefix to cover at least half of the shorter name so
      // short coincidental prefixes (e.g., "Pidov"/"Pidge") don't qualify.
      if (common * 2 < max) continue;
      return `${a.display} → ${b.display}`;
    }
  }

  return null;
}

export function containsEvolutionLine(cards: Card[]): boolean {
  return findEvolutionLine(cards) !== null;
}

/** Total card quantity (sum of card.quantity). */
function bundleCardCount(cards: Card[]): number {
  return cards.reduce((s, c) => s + (c.quantity ?? 1), 0);
}

export function isBulk(cards: Card[]): boolean {
  return bundleCardCount(cards) >= BULK_CARD_THRESHOLD;
}

export function isCommonUncommonOnly(cards: Card[]): boolean {
  if (cards.length === 0) return false;
  return cards.every((c) => c.rarity === "Common" || c.rarity === "Uncommon");
}

/** True when the product type matches one of the structured auto-build profiles. */
export function isRepeatableStructure(productType: ProductType): boolean {
  return productType in PRODUCT_TYPE_AUTO_BUILD;
}

/**
 * Coefficient of variation (stdev / mean) of card market prices, weighted by
 * quantity. Returns 0 when fewer than 2 priced cards exist or the mean is 0.
 */
export function valueVariance(cards: Card[]): number {
  const samples: number[] = [];
  for (const c of cards) {
    const price = c.marketPrice;
    if (!price || price <= 0) continue;
    const qty = Math.max(1, c.quantity ?? 1);
    for (let i = 0; i < qty; i++) samples.push(price);
  }
  if (samples.length < 2) return 0;
  const mean = samples.reduce((s, v) => s + v, 0) / samples.length;
  if (mean <= 0) return 0;
  const variance =
    samples.reduce((s, v) => s + (v - mean) * (v - mean), 0) / samples.length;
  return Math.sqrt(variance) / mean;
}

export interface DefaultListingTypeResult {
  listingType: "FixedPrice" | "Auction";
  reason: string;
}

/**
 * Apply the smart-default rules in order to choose Auction vs Fixed Price for
 * a fresh Go Live form. Returns the chosen type plus a short user-facing
 * reason explaining the choice.
 */
export function pickDefaultListingType(
  cards: Card[],
  productType: ProductType,
): DefaultListingTypeResult {
  // Auction conditions first.
  const popular = findPopularPokemon(cards);
  if (popular) {
    return {
      listingType: "Auction",
      reason: `Defaulted to Auction — popular Pokémon detected (${popular})`,
    };
  }
  if (containsHoloReverseMix(cards)) {
    return {
      listingType: "Auction",
      reason: "Defaulted to Auction — holo/reverse mix detected",
    };
  }
  const line = findEvolutionLine(cards);
  if (line) {
    return {
      listingType: "Auction",
      reason: `Defaulted to Auction — evolution line detected (${line})`,
    };
  }

  // Fixed Price conditions.
  const count = bundleCardCount(cards);
  if (isBulk(cards)) {
    return {
      listingType: "FixedPrice",
      reason: `Defaulted to Fixed Price — bulk lot (${count} cards)`,
    };
  }
  if (isCommonUncommonOnly(cards)) {
    return {
      listingType: "FixedPrice",
      reason: "Defaulted to Fixed Price — common/uncommon only",
    };
  }
  if (isRepeatableStructure(productType)) {
    return {
      listingType: "FixedPrice",
      reason: `Defaulted to Fixed Price — standard ${productType}`,
    };
  }

  // Variance fallback.
  const cv = valueVariance(cards);
  if (cv > VALUE_VARIANCE_AUCTION_THRESHOLD) {
    return {
      listingType: "Auction",
      reason: `Defaulted to Auction — high price variance across cards`,
    };
  }
  return {
    listingType: "FixedPrice",
    reason: "Defaulted to Fixed Price — consistent card values",
  };
}

// ─── Orchestrator ─────────────────────────────────────────────────────────────

// Studio strategies (random-value-lot, casual-prebuilt-deck, etc.) are
// intentionally absent here — they are generated by productOpportunityEngine.ts
// and never participate in the legacy generateBundles orchestrator. The Partial
// type lets new union members exist without requiring a generator entry.
const STRATEGY_FUNCTIONS: Partial<
  Record<
    BundleStrategyId,
    (
      cards: Card[],
      usedIds: Set<string>,
      lowerBound: number,
      targetCount?: number,
    ) => BundleSuggestion[]
  >
> = {
  "same-set": generateSetBundles,
  "rarity-tier": generateRarityBundles,
  "pokemon-type": generatePokemonTypeBundles,
  "pokemon-trainer": generatePokemonTrainerBundles,
  "pokemon-energy": generatePokemonEnergyBundles,
  "pokemon-series": generatePokemonSeriesBundles,
  "pokemon-family": generatePokemonFamilyBundles,
};

// ─── Product-type Auto-Build ──────────────────────────────────────────────────
//
// When the seller picks a product type chip on the Build Product screen, we
// auto-assemble a card selection that matches the product type's shape (target
// count + a foil-mix rule). This is independent of the ranked bundle engine
// above — it's a one-tap convenience for the seller.

export const FOIL_PRICE_CEILING = 10;

export interface ProductTypeAutoBuildProfile {
  /** Target total card *quantity* (sum of card.quantity), not card row count. */
  targetCount: number;
  /** How many of the target should be sub-$10 foils. 0 = no foil rule. */
  foilTarget: number;
  /** Optional filler preference. */
  prefer?: "trainer" | "commonUncommon";
  /** Special source mode for product types that aren't standard "lots". */
  source?: "holoReverseSub10" | "playset";
}

export const PRODUCT_TYPE_AUTO_BUILD: Partial<
  Record<ProductType, ProductTypeAutoBuildProfile>
> = {
  "100-card Random Lot": { targetCount: 100, foilTarget: 9 },
  "50-card Themed Lot": { targetCount: 50, foilTarget: 5 },
  "25-card Trainer Lot": { targetCount: 25, foilTarget: 2, prefer: "trainer" },
  "Holo/Reverse Bundle": {
    targetCount: 10,
    foilTarget: 0,
    source: "holoReverseSub10",
  },
  "4x Playset": { targetCount: 4, foilTarget: 0, source: "playset" },
  "Mystery Pack": { targetCount: 20, foilTarget: 2 },
  "Starter/Kid Pack": {
    targetCount: 50,
    foilTarget: 4,
    prefer: "commonUncommon",
  },
};

/** Maps each lot-shaped Product Type to its weighted-picker profile. */
export const PRODUCT_TYPE_TO_PROFILE: Partial<
  Record<ProductType, WeightProfileName>
> = {
  "100-card Random Lot": "bulk",
  "50-card Themed Lot": "standard",
  "25-card Trainer Lot": "trainer",
  "Mystery Pack": "standard",
  "Starter/Kid Pack": "bulk",
};

export type ShortfallKind =
  | "total"
  | "subTenFoils"
  | "trainer"
  | "commonUncommon"
  | "holoReverseSub10"
  | "playset"
  | "bucketMin";

export interface ShortfallItem {
  kind: ShortfallKind;
  /** Short human-readable category, e.g. "sub-$10 foils", "Trainer cards". */
  label: string;
  /** Required count for this requirement. */
  target: number;
  /** How many were actually available in the seller's unlisted inventory. */
  available: number;
  /** Pre-formatted user-facing line, e.g. "Missing 6 sub-$10 foils — only 3 available." */
  message: string;
}

/**
 * Per-card explanation of why a card was added to the auto-built selection.
 * Extends the weighted picker's `PickReason` with kinds emitted by the
 * special-source modes (playset, sub-$10 holo/reverse) so the UI can explain
 * every selection regardless of which path produced it.
 */
export type AutoBuildPickReasonKind =
  | PickReason["kind"]
  | "playset"
  | "holoReverseFoil";

export interface AutoBuildPickReason {
  kind: AutoBuildPickReasonKind;
  /** Short, user-facing label suitable for a small badge next to the card. */
  label: string;
}

export interface AutoBuildResult {
  cards: Card[];
  selectedQty: number;
  /** Null when the helper fully met the target; otherwise a short user-visible note. */
  shortfallNote: string | null;
  /**
   * Structured per-requirement shortfalls. Empty when every requirement was
   * fully satisfied. Callers should prefer this over `shortfallNote` when
   * rendering a multi-line warning UI.
   */
  shortfalls: ShortfallItem[];
  /**
   * Bundle summary (rarity counts + estimated market value) for the picked
   * cards, when the picker produced a selection. Null for special-source
   * modes that don't go through the weighted picker.
   */
  summary?: BundleSummary | null;
  /**
   * Per-card explanation of why each card was selected, keyed by `card.id`.
   * Empty when the helper produced no selection.
   */
  reasons: Record<string, AutoBuildPickReason>;
}

function sumQty(cards: Card[]): number {
  return cards.reduce((s, c) => s + c.quantity, 0);
}

function isEligibleSubTenFoil(c: Card): boolean {
  return (
    (c.finish === "Holo" || c.finish === "Reverse Holo") &&
    c.marketPrice < FOIL_PRICE_CEILING
  );
}

/**
 * Greedy strict no-overshoot picker. Inventory rows are atomic (a Card row is
 * always selected as a whole, with its full quantity), so when row quantities
 * don't divide evenly into `target` we settle for the best under-target
 * selection rather than overshooting. Caller is expected to surface a
 * shortfall note when `total < target`.
 */
function pickByQuantity(
  pool: Card[],
  target: number,
): { picked: Card[]; total: number } {
  const picked: Card[] = [];
  let total = 0;
  if (target <= 0) return { picked, total };
  for (const c of pool) {
    if (total >= target) break;
    if (total + c.quantity <= target) {
      picked.push(c);
      total += c.quantity;
    }
  }
  return { picked, total };
}

/**
 * Auto-build a card selection for the given product type from the seller's
 * unlisted inventory. Returns null for product types that aren't lot-shaped
 * (Premium Single, Sealed Bundle, Custom).
 */
export function autoBuildSelectionForProductType(
  productType: ProductType,
  unlistedCards: Card[],
  mixOverride?: MixOverride,
): AutoBuildResult | null {
  const profile = PRODUCT_TYPE_AUTO_BUILD[productType];
  if (!profile) return null;

  const pool = unlistedCards.filter((c) => c.quantity > 0);

  // 4x Playset: pick the highest-value sub-$10 card with quantity >= 4. The
  // selection model on this screen is row-atomic (Product.cardIds is a
  // membership set used by sell/hold/unpack flows app-wide; supporting
  // partial-quantity selection would require schema changes that are out of
  // scope for this task). To honor the spec's "up to 4 copies" intent within
  // those constraints we prefer rows with exactly 4 copies and only fall back
  // to rows with more copies when no exact-4 row exists, surfacing a note so
  // the seller knows the lot will include extra copies they may want to trim
  // by adjusting inventory before saving.
  if (profile.source === "playset") {
    const eligible = pool.filter(
      (c) => c.quantity >= 4 && c.marketPrice < FOIL_PRICE_CEILING,
    );
    if (eligible.length === 0) {
      const note = `No unlisted cards with 4+ copies under $${FOIL_PRICE_CEILING} available for a Playset.`;
      return {
        cards: [],
        selectedQty: 0,
        shortfallNote: note,
        shortfalls: [
          {
            kind: "playset",
            label: "Playset candidates",
            target: 4,
            available: 0,
            message: `Missing Playset candidates — 0 unlisted cards with 4+ copies under $${FOIL_PRICE_CEILING} available (target 4).`,
          },
        ],
        reasons: {},
      };
    }
    const exactFours = eligible.filter((c) => c.quantity === 4);
    const sortedByValue = [
      ...(exactFours.length > 0 ? exactFours : eligible),
    ].sort((a, b) => b.marketPrice - a.marketPrice);
    const chosen = sortedByValue[0];
    const note =
      chosen.quantity > 4
        ? `Best playset candidate has ${chosen.quantity} copies (no exact 4x available in inventory) — split the row in inventory first if you only want 4.`
        : null;
    return {
      cards: [chosen],
      selectedQty: chosen.quantity,
      shortfallNote: note,
      shortfalls: [],
      reasons: {
        [chosen.id]: {
          kind: "playset",
          label: "Best playset candidate",
        },
      },
    };
  }

  // Holo/Reverse Bundle: target is built entirely from sub-$10 foils.
  if (profile.source === "holoReverseSub10") {
    const foilPool = pool
      .filter(isEligibleSubTenFoil)
      .sort((a, b) => b.marketPrice - a.marketPrice);
    const availableQty = sumQty(foilPool);
    const { picked, total } = pickByQuantity(foilPool, profile.targetCount);
    const shortfalls: ShortfallItem[] = [];
    let note: string | null = null;
    if (total < profile.targetCount) {
      const missing = profile.targetCount - availableQty;
      const message =
        missing > 0
          ? `Missing ${missing} holo/reverse cards — only ${availableQty} available (target ${profile.targetCount}).`
          : `Missing holo/reverse cards — only ${total} could be selected (target ${profile.targetCount}).`;
      shortfalls.push({
        kind: "holoReverseSub10",
        label: "holo/reverse cards",
        target: profile.targetCount,
        available: availableQty,
        message,
      });
      note = `Only ${total} sub-$${FOIL_PRICE_CEILING} Holo/Reverse cards available — added all of them.`;
    }
    const reasons: Record<string, AutoBuildPickReason> = {};
    for (const c of picked) {
      reasons[c.id] = {
        kind: "holoReverseFoil",
        label: `Sub-$${FOIL_PRICE_CEILING} holo/reverse foil`,
      };
    }
    return {
      cards: picked,
      selectedQty: total,
      shortfallNote: note,
      shortfalls,
      reasons,
    };
  }

  // Standard lot: weighted bundle picker. Profile (standard / bulk / trainer /
  // energy / pokemonType / premium) controls bucket proportions, min
  // guarantees, and per-card duplicate caps. Profile-specific pre-filters
  // (trainer / energy / pokemonType) trim the candidate pool first.
  const profileName = PRODUCT_TYPE_TO_PROFILE[productType];
  if (!profileName) {
    // Should not happen: every entry in PRODUCT_TYPE_AUTO_BUILD that doesn't
    // use a special source must be mapped here. Fall back to an empty result
    // so callers don't crash on an unmapped product type.
    return {
      cards: [],
      selectedQty: 0,
      shortfallNote: null,
      shortfalls: [],
      summary: null,
      reasons: {},
    };
  }

  const weightedProfile = buildWeightedProfile(
    profileName,
    profile.targetCount,
    mixOverride,
  );
  const pickResult = pickWeightedBundle({
    profile: weightedProfile,
    profileName,
    unlistedCards: pool,
  });

  const shortfalls: ShortfallItem[] = [];
  const noteParts: string[] = [];

  if (pickResult.totalShortfall > 0) {
    const message = `Only ${pickResult.selectedQty} unlisted cards available — couldn't reach target of ${profile.targetCount}.`;
    shortfalls.push({
      kind: "total",
      label: "total cards",
      target: profile.targetCount,
      available: pickResult.selectedQty,
      message,
    });
    noteParts.push(message);
  }

  for (const bs of pickResult.bucketShortfalls) {
    shortfalls.push({
      kind: "bucketMin",
      label: bs.bucket,
      target: bs.target,
      available: bs.filled,
      message: bs.message,
    });
    noteParts.push(bs.message);
  }

  const shortfallNote = noteParts.length > 0 ? noteParts.join(" ") : null;
  const summary = summarizeBundle(pickResult.cards);

  return {
    cards: pickResult.cards,
    selectedQty: pickResult.selectedQty,
    shortfallNote,
    shortfalls,
    summary,
    reasons: pickResult.reasons,
  };
}

// ─── Orchestrator (continued) ─────────────────────────────────────────────────

export interface GenerateBundlesOptions {
  /**
   * When true, lowers each strategy's qualifying threshold to 80% of its
   * targetCount (`partialFloor`) so partially-fulfilled bundles are returned
   * with `availabilityPct < 100`. Default false preserves the legacy "only
   * 100%-buildable bundles" semantics that the Build Bundle and Edit Product
   * flows rely on.
   */
  includePartial?: boolean;
}

export function generateBundles(
  cards: Card[],
  settings: BundleSettings,
  options?: GenerateBundlesOptions,
): BundleSuggestion[] {
  if (!settings.globalEnabled) return [];

  const unlisted = cards.filter(
    (c) => c.listingStatus === "Unlisted" && c.quantity > 0,
  );
  if (unlisted.length === 0) return [];

  const usedIds = new Set<string>();
  const results: BundleSuggestion[] = [];

  const orderedStrategies = [...settings.strategies]
    .filter((s) => s.enabled)
    .sort((a, b) => a.priority - b.priority);

  const includePartial = options?.includePartial ?? false;

  for (const strategy of orderedStrategies) {
    const fn = STRATEGY_FUNCTIONS[strategy.id];
    if (!fn) continue;

    const target =
      strategy.id === "same-set"
        ? settings.sameSetMinCards
        : (STRATEGY_META.find((m) => m.id === strategy.id)?.defaultMinCards ??
          5);

    const lowerBound = includePartial ? partialFloor(target) : target;
    const suggestions = fn(unlisted, usedIds, lowerBound, target);

    for (const suggestion of suggestions) {
      for (const card of suggestion.cards) {
        usedIds.add(card.id);
      }
      results.push(suggestion);
    }
  }

  return results.sort((a, b) => b.confidenceScore - a.confidenceScore);
}
